import React from 'react';
import { Box, Card, CardHeader, CardContent, Typography, CircularProgress, Stack } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { translate as t } from 'hooks/useTranslations';

dayjs.extend(customParseFormat);
import { useRiskPoolOrgContext } from '../../RiskPoolOrgContext';
import { usePolicyContext } from '../PolicyContext';
import { getPeriodMode } from '../_data_sources/usePolicyTrends';
import type { PeriodMode } from '../_data_sources/usePolicyTrends';
import useAssignments from '../_data_sources/useAssignments';
import { CHART_COLORS } from '../_constants';
import { neutral, success, error } from '_lex/_foundations/phxColors';

export function AssignmentsChart() {
  const { orgId, queryParams } = useRiskPoolOrgContext();
  const { manualId } = usePolicyContext();
  const { data, isLoading } = useAssignments(orgId, {
    ...queryParams,
    manualId: manualId !== 'all' ? manualId : undefined,
  });

  if (isLoading) {
    return (
      <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}`, flex: 1 }}>
        <CardHeader title={t('dashboard.riskPool.riskPoolOrg.assignments.title')} />
        <CardContent>
          <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const metrics = data?.metrics ?? [];
  if (metrics.length === 0) {
    return null;
  }

  // One x-axis entry per period (e.g. one per week). Use period as id so labels don't collapse.
  const xAxisData = metrics.map((point) => point.period);
  const periodMode: PeriodMode = getPeriodMode(queryParams.startDate ?? '', queryParams.endDate ?? '');
  // Date-only strings (YYYY-MM-DD) parsed as local time. Weeks = start-of-week as MM/DD/YYYY, months = MMM YYYY.
  const formatAxisPeriod = (period: string) =>
    periodMode === 'weeks'
      ? dayjs(period, 'YYYY-MM-DD', true).format('MM/DD/YYYY')
      : dayjs(period, 'YYYY-MM-DD', true).format('MMM YYYY');

  const series = [
    {
      id: 'completed',
      label: t('dashboard.riskPool.riskPoolOrg.assignments.completed'),
      data: metrics.map((d) => d.completedCount),
      color: CHART_COLORS.completedLine,
      area: true,
      showMark: false,
    },
    {
      id: 'overdue',
      label: t('dashboard.riskPool.riskPoolOrg.assignments.overdue'),
      data: metrics.map((d) => d.overdueCount),
      color: CHART_COLORS.overdueLine,
      area: true,
      showMark: false,
    },
  ];

  // Legend data with breakdowns
  const legendItems = [
    {
      label: t('dashboard.riskPool.riskPoolOrg.assignments.completed'),
      color: CHART_COLORS.completedLine,
      breakdown: [
        {
          label: t('dashboard.riskPool.riskPoolOrg.assignments.acknowledgments'),
          value: metrics.reduce((sum, d) => sum + d.acknowledgments.completedCount, 0),
        },
        {
          label: t('dashboard.riskPool.riskPoolOrg.assignments.dtbs'),
          value: metrics.reduce((sum, d) => sum + d.dtbs.completedCount, 0),
        },
      ],
    },
    {
      label: t('dashboard.riskPool.riskPoolOrg.assignments.overdue'),
      color: CHART_COLORS.overdueLine,
      breakdown: [
        {
          label: t('dashboard.riskPool.riskPoolOrg.assignments.acknowledgments'),
          value: metrics.reduce((sum, d) => sum + d.acknowledgments.overdueCount, 0),
        },
        {
          label: t('dashboard.riskPool.riskPoolOrg.assignments.dtbs'),
          value: metrics.reduce((sum, d) => sum + d.dtbs.overdueCount, 0),
        },
      ],
    },
  ];

  return (
    <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}`, flex: 1 }}>
      <CardHeader title={t('dashboard.riskPool.riskPoolOrg.assignments.title')} />
      <CardContent>
        {/* Legend */}
        <Stack
          direction='row'
          spacing={4}
          sx={{ mb: 3, justifyContent: 'center' }}
        >
          {legendItems.map((item) => (
            <Box
              key={item.label}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  color: neutral[500],
                  fontSize: 12,
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  lineHeight: '16.8px',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Box sx={{ width: '100%', height: 250, position: 'relative' }}>
          {/* SVG Gradient Definitions */}
          <svg style={{ position: 'absolute', width: 0, height: 0 }}>
            <defs>
              <linearGradient
                id='completedGradient'
                x1='0'
                y1='0'
                x2='0'
                y2='1'
              >
                <stop
                  offset='0%'
                  stopColor={success[600]}
                  stopOpacity={0.3}
                />
                <stop
                  offset='100%'
                  stopColor={success[600]}
                  stopOpacity={0.05}
                />
              </linearGradient>
              <linearGradient
                id='overdueGradient'
                x1='0'
                y1='0'
                x2='0'
                y2='1'
              >
                <stop
                  offset='0%'
                  stopColor={error[700]}
                  stopOpacity={0.3}
                />
                <stop
                  offset='100%'
                  stopColor={error[700]}
                  stopOpacity={0.05}
                />
              </linearGradient>
            </defs>
          </svg>

          <LineChart
            xAxis={[
              {
                scaleType: 'point',
                data: xAxisData,
                valueFormatter: (value: string) => formatAxisPeriod(value),
              },
            ]}
            yAxis={[{ min: 0 }]}
            series={series}
            height={250}
            margin={{ top: 10, bottom: 30, left: 60, right: 10 }}
            grid={{ horizontal: false, vertical: false }}
            slotProps={{
              legend: { hidden: true },
            }}
            sx={{
              '& .MuiLineElement-root': {
                strokeWidth: 2,
              },
              '& .MuiAreaElement-series-completed': {
                fill: 'url(#completedGradient)',
              },
              '& .MuiAreaElement-series-overdue': {
                fill: 'url(#overdueGradient)',
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export default AssignmentsChart;
